'use client'

import { IconCloseMedium } from 'icons/components/IconCloseMedium'
import { StoreAddress } from 'maps/components/commons/StoreAddress/StoreAddress'
import { StoreInfo } from 'maps/components/commons/StoreInfo/StoreInfo'
import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import type { Store } from 'maps/types'
import type React from 'react'
import {
	type AnimationEvent,
	type KeyboardEvent,
	forwardRef,
	useEffect,
	useState,
} from 'react'
import { Keyboard } from 'types/keyboard'
import { cx } from 'utils/cx'

import styles from './MarkerCard.module.scss'

interface MarkerCardProps {
	readonly onConfirm?: (store: Store) => void
}

export const MarkerCard = forwardRef<HTMLDivElement, MarkerCardProps>(
	function MarkerCard({ onConfirm }, ref): React.ReactNode | null {
		const { expanded, setExpanded } = useExpandedStore()
		/** Use a copy of the expanded store to control the animation. */
		const [card, setCard] = useState<Store | undefined>(undefined)

		const ariaIds = {
			descriptionId: `store-${expanded?.id}-description`,
			extendedInfoId: `store-${expanded?.id}-extended-info`,
		}

		const handleOnClose = () => {
			// Activates closing animation
			setExpanded()
		}

		const handleOnKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
			if (event.key === Keyboard.Tab) {
				event.preventDefault()
			}
		}

		const handleOnAnimationEnd = (e: AnimationEvent<HTMLElement>) => {
			if (e.animationName.includes('closing')) {
				// Deselect the expanded marker(store) and close its card
				setCard(undefined)
			}
		}

		useEffect(() => {
			if (expanded) {
				setCard(expanded)
			}
		}, [expanded])

		return (
			<div
				id='card'
				role='presentation'
				ref={ref}
				className={cx(styles.card, !expanded && styles.closing)}
				onAnimationEnd={handleOnAnimationEnd}
				data-testid='maps.marker.card'
				tabIndex={-1}
			>
				{card && (
					<div className={styles.content}>
						<StoreAddress store={card} close />
						<StoreInfo store={card} onConfirm={onConfirm} ariaIds={ariaIds} />
						<button
							className={styles.close}
							type='button'
							onClick={handleOnClose}
							onKeyDown={handleOnKeyDown}
						>
							<IconCloseMedium lang='en' alt='Close' />
						</button>
					</div>
				)}
			</div>
		)
	}
)
